import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, UserX, Send, CheckCircle2, Briefcase, Building2, Users, ClipboardList, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/EmptyState";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

import { confirmAction } from "@/lib/dialogs";
const STATUS = {
  PRESENT: { label: "P", full: "Present", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  HALF_DAY: { label: "½", full: "Half day", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  ABSENT: { label: "A", full: "Absent", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-400" },
};
const CYCLE = ["PRESENT", "HALF_DAY", "ABSENT"];
const NIGHT_CLS = "bg-indigo-500/20 text-indigo-700 dark:text-indigo-300";
const thisMonth = () => new Date().toISOString().slice(0, 7);

export function LabourManagementTab({ projectId }) {
  const [section, setSection] = useState("contractor");
  const [workOrders, setWorkOrders] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/procurement/work-orders"),
      api.get("/procurement/vendors").catch(() => ({ data: [] })),
    ]).then(([woRes, vendorRes]) => {
      const vendorName = Object.fromEntries((vendorRes.data || []).map((v) => [v.id, v.name]));
      setWorkOrders(
        (woRes.data || [])
          .filter((w) => w.project_id === projectId && w.status === "APPROVED")
          .map((w) => ({ ...w, vendor_name: vendorName[w.vendor_id] || "vendor" }))
      );
    }).catch(() => {});
  }, [projectId]);

  const TABS = [
    ["contractor", "Contractor", Briefcase],
    ["company", "Company", Building2],
  ];

  return (
    <div className="space-y-4" data-testid="labour-management-tab">
      <div className="inline-flex flex-wrap rounded-lg border border-border bg-muted p-1">
        {TABS.map(([v, label, Icon]) => (
          <button
            key={v} type="button" onClick={() => setSection(v)} data-testid={`labour-mgmt-section-${v}`}
            className={cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors", section === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {section === "contractor" && <ContractorSection projectId={projectId} workOrders={workOrders} />}
      {section === "company" && <CompanySection projectId={projectId} />}
    </div>
  );
}

/* ========================================================================== COMPANY (wraps Labours / Management / Approve / Attendance) */
function CompanySection({ projectId }) {
  const [tab, setTab] = useState("labours");
  const TABS = [
    ["labours", "Labours", Users],
    ["management", "Management", ClipboardList],
    ["approve", "Approve", CheckCircle2],
    ["attendance", "Attendance", CalendarCheck],
  ];
  return (
    <div className="space-y-3">
      <div className="inline-flex flex-wrap rounded-lg border border-border bg-muted p-1">
        {TABS.map(([v, label, Icon]) => (
          <button key={v} type="button" onClick={() => setTab(v)} className={cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium", tab === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")} data-testid={`company-tab-${v}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {tab === "labours" && <LaboursSection projectId={projectId} />}
      {tab === "management" && <ManagementSection projectId={projectId} />}
      {tab === "approve" && <ApproveSection projectId={projectId} />}
      {tab === "attendance" && <CompanyAttendanceSection projectId={projectId} />}
    </div>
  );
}

/* ========================================================================== CONTRACTOR (unchanged behaviour) */
function ContractorSection({ projectId, workOrders }) {
  const [tab, setTab] = useState("workers");
  return (
    <div className="space-y-3">
      <div className="inline-flex rounded-lg border border-border bg-muted p-1">
        {[["workers", "Workers"], ["attendance", "Attendance"], ["mandays", "Man-days"]].map(([v, label]) => (
          <button key={v} type="button" onClick={() => setTab(v)} className={cn("rounded-md px-3 py-1.5 text-sm font-medium", tab === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")} data-testid={`contractor-tab-${v}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "workers" && <ContractorWorkers projectId={projectId} workOrders={workOrders} />}
      {tab === "attendance" && <ContractorAttendance projectId={projectId} workOrders={workOrders} />}
      {tab === "mandays" && <ContractorManDays projectId={projectId} workOrders={workOrders} />}
    </div>
  );
}

function ContractorWorkers({ projectId, workOrders }) {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const woNumber = (id) => workOrders.find((w) => w.id === id)?.number || "—";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/labour-workers`);
      setWorkers(data || []);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const deactivate = async (w) => {
    if (!await confirmAction(`Remove ${w.name} from active labour?`)) return;
    try {
      await api.post(`/projects/${projectId}/labour-workers/${w.id}/deactivate`);
      toast.success("Worker deactivated");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><AddContractorWorkerDialog projectId={projectId} workOrders={workOrders} onCreated={load} /></div>
      <div className="rounded-xl border border-border bg-card">
        {!loading && workers.length === 0 ? (
          <EmptyState title="No contractor workers yet" testId="contractor-workers-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Trade</TableHead><TableHead>Work Order</TableHead><TableHead className="text-right">Daily rate</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {workers.map((w) => (
                <TableRow key={w.id} className={!w.is_active ? "opacity-50" : ""} data-testid={`contractor-worker-row-${w.id}`}>
                  <TableCell className="text-foreground">{w.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{w.trade}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{woNumber(w.work_order_id)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">₹{fmtNum(w.daily_rate)}</TableCell>
                  <TableCell className="text-right">
                    {w.is_active && <Button size="sm" variant="ghost" onClick={() => deactivate(w)} data-testid={`deactivate-contractor-worker-${w.id}`}><UserX className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function AddContractorWorkerDialog({ projectId, workOrders, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ work_order_id: "", name: "", trade: "", daily_rate: "" });
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.work_order_id) {
      toast.error("Pick a Work Order first");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/labour-workers`, { ...form, daily_rate: Number(form.daily_rate) });
      toast.success("Worker added");
      setForm({ work_order_id: form.work_order_id, name: "", trade: "", daily_rate: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button disabled={workOrders.length === 0} data-testid="add-contractor-worker-button"><Plus className="h-4 w-4" /> Add Worker</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Add contractor worker</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="add-contractor-worker-form">
          <div>
            <Label>Work Order</Label>
            <Select required value={form.work_order_id} onValueChange={(v) => setForm({ ...form, work_order_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="contractor-worker-wo-select"><SelectValue placeholder={workOrders.length ? "Select" : "No approved Work Orders for this project"} /></SelectTrigger>
              <SelectContent>{workOrders.map((w) => <SelectItem key={w.id} value={w.id}>{w.number} — {w.vendor_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Name</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="contractor-worker-name-input" /></div>
          <div><Label>Trade</Label><Input required value={form.trade} onChange={set("trade")} className="mt-1.5" placeholder="e.g. Mason" data-testid="contractor-worker-trade-input" /></div>
          <div><Label>Daily rate</Label><Input required type="number" step="0.01" min="0.01" value={form.daily_rate} onChange={set("daily_rate")} className="mt-1.5" data-testid="contractor-worker-rate-input" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContractorAttendance({ projectId, workOrders }) {
  const [workOrderId, setWorkOrderId] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/projects/${projectId}/labour-attendance`, { params: { period: `${month}-01`, work_order_id: workOrderId || undefined } });
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, month, workOrderId]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => (data ? Array.from({ length: data.days_in_month }, (_, i) => i + 1) : []), [data]);

  const cycle = async (worker, day) => {
    const existing = worker.days[day]?.status;
    if (worker.days[day]?.billed) {
      toast.error("This day is already on a generated bill");
      return;
    }
    const next = existing ? CYCLE[(CYCLE.indexOf(existing) + 1) % CYCLE.length] : "PRESENT";
    setData((d) => ({ ...d, workers: d.workers.map((w) => (w.id === worker.id ? { ...w, days: { ...w.days, [day]: { status: next, billed: false } } } : w)) }));
    try {
      await api.post(`/projects/${projectId}/labour-attendance`, { worker_id: worker.id, entry_date: `${month}-${String(day).padStart(2, "0")}`, status: next });
    } catch (err) {
      toast.error(getErrorMessage(err));
      load();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label>Work Order</Label>
          <Select value={workOrderId || "all"} onValueChange={(v) => setWorkOrderId(v === "all" ? "" : v)}>
            <SelectTrigger className="mt-1.5 w-56"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{workOrders.map((w) => <SelectItem key={w.id} value={w.id}>{w.number}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5" /></div>
        <div className="ml-auto flex gap-3 text-xs">
          {Object.entries(STATUS).map(([k, v]) => <span key={k} className={cn("rounded px-2 py-1", v.cls)}>{v.label} {v.full}</span>)}
        </div>
      </div>
      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.workers.length === 0 ? (
        <EmptyState title="No active workers to mark" testId="contractor-attendance-empty-state" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full border-collapse text-sm">
            <thead><tr className="border-b border-border"><th className="sticky left-0 z-10 bg-card p-2 text-left">Worker</th>{days.map((d) => <th key={d} className="min-w-[30px] p-1 text-center text-[10px] text-muted-foreground">{d}</th>)}</tr></thead>
            <tbody>
              {data.workers.map((w) => (
                <tr key={w.id} className="border-b border-border/50" data-testid={`contractor-attendance-row-${w.id}`}>
                  <td className="sticky left-0 z-10 bg-card p-2"><div className="text-foreground">{w.name}</div><div className="text-[10px] text-muted-foreground">{w.trade}</div></td>
                  {days.map((d) => {
                    const cell = w.days[d];
                    return (
                      <td key={d} className="p-0.5 text-center">
                        <button type="button" onClick={() => cycle(w, d)} data-testid={`contractor-attendance-cell-${w.id}-${d}`}
                          className={cn("flex h-7 w-7 items-center justify-center rounded text-[11px] font-semibold", cell ? STATUS[cell.status].cls : "bg-muted/40 text-muted-foreground/40", cell?.billed && "ring-1 ring-sky-400")}
                          title={cell ? `${STATUS[cell.status].full}${cell.billed ? " · billed" : ""}` : "Not marked"}>
                          {cell ? STATUS[cell.status].label : "·"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ContractorManDays({ projectId, workOrders }) {
  const [form, setForm] = useState({ work_order_id: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  useEffect(() => {
    if (!form.work_order_id) { setData(null); return; }
    let live = true;
    setLoading(true);
    const params = { work_order_id: form.work_order_id };
    if (form.date_from) params.date_from = form.date_from;
    if (form.date_to) params.date_to = form.date_to;
    api.get(`/projects/${projectId}/labour-management/contractor-mandays`, { params })
      .then(({ data: d }) => { if (live) setData(d); })
      .catch((err) => { if (live) { setData(null); toast.error(getErrorMessage(err)); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [projectId, form.work_order_id, form.date_from, form.date_to]);

  return (
    <div className="space-y-3" data-testid="contractor-mandays">
      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        Contractor labour is <span className="font-medium text-foreground">not billed here</span>. The contractor is paid only through
        <span className="font-medium text-foreground"> Work Order → JMR → Service Bill</span>. Use these man-days as the measured quantity
        when your Work Order has a "Labour supply, per man-day" line.
      </div>
      <div className="grid max-w-3xl gap-3 sm:grid-cols-3">
        <div>
          <Label>Work Order</Label>
          <Select value={form.work_order_id} onValueChange={(v) => setForm({ ...form, work_order_id: v })}>
            <SelectTrigger className="mt-1.5" data-testid="mandays-wo-select"><SelectValue placeholder={workOrders.length ? "Select" : "No approved Work Orders"} /></SelectTrigger>
            <SelectContent>{workOrders.map((w) => <SelectItem key={w.id} value={w.id}>{w.number}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>From</Label><Input type="date" value={form.date_from} onChange={set("date_from")} className="mt-1.5" /></div>
        <div><Label>To</Label><Input type="date" value={form.date_to} onChange={set("date_to")} className="mt-1.5" /></div>
      </div>
      {!form.work_order_id ? null : loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.workers.length === 0 ? (
        <EmptyState title="No workers on this Work Order" testId="mandays-empty-state" />
      ) : (
        <div className="max-w-3xl rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>Worker</TableHead><TableHead>Trade</TableHead><TableHead className="text-right">Present</TableHead><TableHead className="text-right">Half</TableHead><TableHead className="text-right">Man-days</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.workers.map((w) => (
                <TableRow key={w.id}>
                  <TableCell className="text-foreground">{w.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{w.trade}</TableCell>
                  <TableCell className="text-right tabular-nums">{w.present}</TableCell>
                  <TableCell className="text-right tabular-nums">{w.half_days}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{fmtNum(w.man_days)}</TableCell>
                </TableRow>
              ))}
              <TableRow><TableCell colSpan={4} className="text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total man-days</TableCell>
                <TableCell className="text-right text-base font-semibold tabular-nums" data-testid="mandays-total">{fmtNum(data.total_man_days)}</TableCell></TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/* ========================================================================== LABOURS (worker pool) */
function LaboursSection({ projectId }) {
  const [labours, setLabours] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/labour-master`);
      setLabours(data || []);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const deactivate = async (l) => {
    if (!await confirmAction(`Remove ${l.name} from the pool?`)) return;
    try {
      await api.post(`/projects/${projectId}/labour-master/${l.id}/deactivate`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Register people once here — assign them to specific jobs later in the Approve tab.</p>
      <div className="flex justify-end"><AddLabourDialog projectId={projectId} onCreated={load} /></div>
      <div className="rounded-xl border border-border bg-card">
        {!loading && labours.length === 0 ? (
          <EmptyState title="No labours registered yet" testId="labours-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Labour No.</TableHead><TableHead>Name</TableHead><TableHead>Trade</TableHead><TableHead className="text-right">Daily rate</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {labours.map((l) => (
                <TableRow key={l.id} className={!l.is_active ? "opacity-50" : ""} data-testid={`labour-master-row-${l.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{l.labour_no || "—"}</TableCell>
                  <TableCell className="text-foreground">{l.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{l.trade}</TableCell>
                  <TableCell className="text-right font-mono text-sm">₹{fmtNum(l.daily_rate)}</TableCell>
                  <TableCell className="text-xs">{l.is_active ? "Active" : "Inactive"}</TableCell>
                  <TableCell className="text-right">
                    {l.is_active && <Button size="sm" variant="ghost" onClick={() => deactivate(l)} data-testid={`deactivate-labour-${l.id}`}><UserX className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function AddLabourDialog({ projectId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ labour_no: "", name: "", trade: "", daily_rate: "" });
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/labour-master`, { ...form, daily_rate: Number(form.daily_rate) });
      toast.success("Labour added to the pool");
      setForm({ labour_no: "", name: "", trade: "", daily_rate: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-labour-master-button"><Plus className="h-4 w-4" /> Add Labour</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Add to the labour pool</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="add-labour-master-form">
          <div><Label>Labour No.</Label><Input value={form.labour_no} onChange={set("labour_no")} className="mt-1.5 font-mono" placeholder="Leave blank to auto-generate (LAB-0001)" data-testid="labour-master-no-input" /></div>
          <div><Label>Name</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="labour-master-name-input" /></div>
          <div><Label>Trade</Label><Input required value={form.trade} onChange={set("trade")} className="mt-1.5" placeholder="e.g. Mason" data-testid="labour-master-trade-input" /></div>
          <div><Label>Daily rate</Label><Input required type="number" step="0.01" min="0.01" value={form.daily_rate} onChange={set("daily_rate")} className="mt-1.5" data-testid="labour-master-rate-input" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ========================================================================== MANAGEMENT (pending queue) */
function ManagementSection({ projectId }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${projectId}/labour-management/pending`).then(({ data }) => setPending(data || [])).finally(() => setLoading(false));
  }, [projectId]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Approved "Company Labour" requisitions waiting to have real workers assigned — go to the Approve tab to process one.</p>
      {!loading && pending.length === 0 ? (
        <EmptyState title="Nothing waiting" description="Every Company Labour requisition has workers assigned." testId="management-empty-state" />
      ) : (
        <div className="space-y-3">
          {pending.map((sr) => (
            <div key={sr.id} className="rounded-xl border border-border bg-card p-4" data-testid={`pending-sr-card-${sr.number}`}>
              <p className="font-mono text-xs text-muted-foreground">{sr.number} · {sr.requisition_date}</p>
              <p className="text-sm text-foreground">{sr.scope_of_work}</p>
              <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                {sr.items.map((i) => <li key={i.id}>{i.description} — {i.quantity}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ========================================================================== APPROVE (assign workers + planned dates) */
function ApproveSection({ projectId }) {
  const [pending, setPending] = useState([]);
  const [labours, setLabours] = useState([]);
  const [srId, setSrId] = useState("");
  const [labourIds, setLabourIds] = useState([]);
  const [plannedStart, setPlannedStart] = useState(todayISO());
  const [plannedEnd, setPlannedEnd] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/projects/${projectId}/labour-management/pending`),
      api.get(`/projects/${projectId}/labour-master`),
    ]).then(([pendingRes, labourRes]) => {
      setPending(pendingRes.data || []);
      setLabours((labourRes.data || []).filter((l) => l.is_active));
    });
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const sr = pending.find((s) => s.id === srId);
  const [busyRows, setBusyRows] = useState([]);
  useEffect(() => {
    if (!plannedStart) { setBusyRows([]); return; }
    api.get("/labour-availability", { params: { start: plannedStart, end: plannedEnd || undefined } })
      .then(({ data }) => setBusyRows(data || [])).catch(() => setBusyRows([]));
  }, [plannedStart, plannedEnd, pending]);
  const busy = {};
  busyRows.forEach((r) => { busy[r.labour_id] = r; });
  useEffect(() => { setLabourIds((ids) => ids.filter((id) => !busy[id])); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [busyRows]);
  const addAllFree = () => setLabourIds((ids) => Array.from(new Set([...ids, ...labours.filter((l) => !busy[l.id]).map((l) => l.id)])));
  const toggleLabour = (id) => setLabourIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!srId) {
      toast.error("Pick a requisition first");
      return;
    }
    if (labourIds.length === 0) {
      toast.error("Pick at least one worker");
      return;
    }
    if (!plannedEnd) {
      toast.error("Set a planned end date");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/labour-management/${srId}/assign`, {
        labour_ids: labourIds, planned_start_date: plannedStart, planned_end_date: plannedEnd,
      });
      toast.success(`${sr.number} approved — ${labourIds.length} worker(s) assigned`);
      setSrId(""); setLabourIds([]); setPlannedEnd("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!pending.length) {
    return <EmptyState title="Nothing to approve" description="No Company Labour requisitions are waiting for worker assignment." testId="approve-empty-state" />;
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
    <div className="w-full max-w-lg shrink-0 space-y-3 rounded-xl border border-border bg-card p-4">
      <form onSubmit={submit} className="space-y-3" data-testid="approve-assign-form">
        <div>
          <Label>Requisition</Label>
          <Select required value={srId} onValueChange={setSrId}>
            <SelectTrigger className="mt-1.5" data-testid="approve-sr-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{pending.map((s) => <SelectItem key={s.id} value={s.id}>{s.number} — {s.scope_of_work}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {sr && (
          <>
            <div>
              <Label>Assign workers from the pool</Label>
              {labours.length === 0 ? (
                <p className="mt-1.5 text-xs text-muted-foreground">No active labours in the pool yet — add some in the Labours tab first.</p>
              ) : (
                <div className="mt-1.5 max-h-48 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                  {labours.map((l) => (
                    <label key={l.id} className={`flex items-center gap-2 rounded p-1.5 text-sm hover:bg-muted ${busy[l.id] ? "opacity-50" : ""}`}>
                      <input type="checkbox" checked={labourIds.includes(l.id)} disabled={!!busy[l.id]} onChange={() => toggleLabour(l.id)} data-testid={`assign-labour-checkbox-${l.id}`} />
                      <span>{l.labour_no ? `${l.labour_no} — ` : ""}{l.name} — {l.trade} — ₹{fmtNum(l.daily_rate)}/day</span>
                      {busy[l.id] && <span className="ml-auto shrink-0 text-xs text-amber-600">on {busy[l.id].requisition || "another job"}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Planned start date</Label><Input required type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} className="mt-1.5" data-testid="planned-start-input" /></div>
              <div><Label>Planned end date</Label><Input required type="date" value={plannedEnd} onChange={(e) => setPlannedEnd(e.target.value)} className="mt-1.5" data-testid="planned-end-input" /></div>
            </div>
            <Button type="submit" disabled={saving} className="w-full" data-testid="approve-assign-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve & Assign
            </Button>
          </>
        )}
      </form>
    </div>
    <FreeWorkersCard labours={labours} busy={busy} labourIds={labourIds} onToggle={toggleLabour} onAddAll={addAllFree}
      start={plannedStart} end={plannedEnd} canAdd={!!sr} />
    </div>
  );
}

/* Free workers card: who is not on any job for the planned dates. Picking one ticks them in the pool list on the left. */
function FreeWorkersCard({ labours, busy, labourIds, onToggle, onAddAll, start, end, canAdd }) {
  const free = labours.filter((l) => !busy[l.id]);
  const busyList = labours.filter((l) => busy[l.id]);
  const allAdded = free.length > 0 && free.every((l) => labourIds.includes(l.id));
  return (
    <div className="w-full max-w-md space-y-3 rounded-xl border border-border bg-card p-4" data-testid="free-workers-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Free workers <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid="free-workers-count">{free.length}</span></h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {start ? `Not on any job ${end ? `between ${start} and ${end}` : `on ${start} - set an end date to check the whole period`}` : "Pick the planned dates to see who is free"}
          </p>
        </div>
        {free.length > 0 && (
          <Button type="button" variant="outline" size="sm" disabled={!canAdd || allAdded} onClick={onAddAll} data-testid="free-workers-add-all">Add all</Button>
        )}
      </div>
      {free.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">Everyone in the pool is on a job for these dates.</p>
      ) : (
        <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
          {free.map((l) => {
            const added = labourIds.includes(l.id);
            return (
              <div key={l.id} className="flex items-center justify-between gap-2 rounded-md border border-border p-2 text-sm" data-testid={`free-worker-${l.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">{l.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{l.labour_no ? `${l.labour_no} - ` : ""}{l.trade} - ₹{fmtNum(l.daily_rate)}/day</p>
                </div>
                <Button type="button" size="sm" variant={added ? "secondary" : "outline"} disabled={!canAdd && !added} onClick={() => onToggle(l.id)} data-testid={`free-worker-toggle-${l.id}`}>
                  {added ? "Added ✓" : "Add"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      {!canAdd && free.length > 0 && <p className="text-xs text-muted-foreground">Pick a requisition on the left to start adding workers.</p>}
      {busyList.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none font-medium">On another job ({busyList.length})</summary>
          <ul className="mt-2 space-y-1">
            {busyList.map((l) => (
              <li key={l.id} className="flex justify-between gap-2 opacity-80">
                <span className="truncate">{l.name}</span>
                <span className="shrink-0">{busy[l.id].requisition || "job"} · {busy[l.id].planned_start_date} to {busy[l.id].planned_end_date}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/* ========================================================================== ATTENDANCE (per assignment) + Generate Bill */
function CompanyAttendanceSection({ projectId }) {
  const [mark, setMark] = useState({ shift: "DAY", ot: "" });
  const [assignments, setAssignments] = useState([]);
  const [assignmentId, setAssignmentId] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAssignments = useCallback(() => {
    api.get(`/projects/${projectId}/labour-management/assignments`).then(({ data }) => setAssignments(data || []));
  }, [projectId]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/projects/${projectId}/company-attendance`, { params: { period: `${month}-01`, assignment_id: assignmentId || undefined } });
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, month, assignmentId]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => (data ? Array.from({ length: data.days_in_month }, (_, i) => i + 1) : []), [data]);

  const removeMember = async (member) => {
    if (!await confirmAction(`Remove ${member.name} from ${member.sr_number}? They become free for other jobs straight away.`)) return;
    try {
      await api.delete(`/projects/${projectId}/labour-management/assignments/${member.assignment_id}/members/${member.id}`);
      toast.success(`${member.name} removed from ${member.sr_number}`);
      loadAssignments();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const cycle = async (member, day, ev) => {
    if (member.planned_start && `${month}-${String(day).padStart(2, "0")}` < member.planned_start) {
      toast.error(`This job starts on ${member.planned_start} - attendance can't be marked before it`);
      return;
    }
    const existing = member.days[day]?.status;
    if (member.days[day]?.billed) {
      toast.error("This day is already on a generated bill");
      return;
    }
    const keep = !!(ev && ev.shiftKey && existing && existing !== "ABSENT");
    const next = keep ? existing : existing ? CYCLE[(CYCLE.indexOf(existing) + 1) % CYCLE.length] : "PRESENT";
    const shift = next === "ABSENT" ? "DAY" : mark.shift;
    const ot = next === "ABSENT" ? 0 : Number(mark.ot || 0);
    setData((d) => ({ ...d, members: d.members.map((m) => (m.id === member.id ? { ...m, days: { ...m.days, [day]: { status: next, shift, ot_hours: ot, billed: false } } } : m)) }));
    try {
      await api.post(`/projects/${projectId}/company-attendance`, { member_id: member.id, entry_date: `${month}-${String(day).padStart(2, "0")}`, status: next, shift, ot_hours: ot });
    } catch (err) {
      toast.error(getErrorMessage(err));
      load();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label>Assignment</Label>
          <Select value={assignmentId || "all"} onValueChange={(v) => setAssignmentId(v === "all" ? "" : v)}>
            <SelectTrigger className="mt-1.5 w-64"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{assignments.map((a) => <SelectItem key={a.id} value={a.id}>{a.sr_number} ({a.members.length} worker{a.members.length === 1 ? "" : "s"})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5" /></div>
        {assignmentId && (
          <AddWorkersToJobDialog
            projectId={projectId} assignmentId={assignmentId}
            assignmentLabel={assignments.find((a) => a.id === assignmentId)?.sr_number}
            existingLabourIds={assignments.find((a) => a.id === assignmentId)?.members.map((m) => m.labour_id) || []}
            onAdded={() => { loadAssignments(); load(); }}
          />
        )}
        {assignmentId && (
          <CompleteJobControl
            projectId={projectId} assignment={assignments.find((x) => x.id === assignmentId)}
            onDone={() => { loadAssignments(); load(); }}
          />
        )}
        {assignmentId && (
          <ExtendJobControl
            projectId={projectId} assignment={assignments.find((x) => x.id === assignmentId)}
            members={data?.members || []} month={month}
            onDone={() => { loadAssignments(); load(); }}
          />
        )}
        {assignmentId && (
          <ClearMarksControl
            projectId={projectId} assignment={assignments.find((x) => x.id === assignmentId)}
            onDone={() => { loadAssignments(); load(); }}
          />
        )}
        <div className="ml-auto flex gap-3 text-xs">
          {Object.entries(STATUS).map(([k, v]) => <span key={k} className={cn("rounded px-2 py-1", v.cls)}>{v.label} {v.full}</span>)}
        </div>
      </div>
      <ShiftMarkBar projectId={projectId} mark={mark} setMark={setMark} />
      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.members.length === 0 ? (
        <EmptyState title="No assigned workers to mark" description="Approve a requisition in the Approve tab first." testId="company-attendance-empty-state" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full border-collapse text-sm">
            <thead><tr className="border-b border-border"><th className="sticky left-0 z-10 bg-card p-2 text-left">Worker</th>{days.map((d) => <th key={d} className="min-w-[30px] p-1 text-center text-[10px] text-muted-foreground">{d}</th>)}</tr></thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id} className="border-b border-border/50" data-testid={`company-attendance-row-${m.id}`}>
                  <td className="sticky left-0 z-10 bg-card p-2"><div className="flex items-start justify-between gap-2"><div><div className="text-foreground">{m.name}</div><div className="text-[10px] text-muted-foreground">{m.labour_no ? `${m.labour_no} · ` : ""}{m.trade} · {m.sr_number}</div></div><button type="button" title="Remove from job" aria-label={`Remove ${m.name} from job`} onClick={() => removeMember(m)} className="shrink-0 rounded px-1.5 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive" data-testid={`remove-member-${m.id}`}>✕</button></div></td>
                  {days.map((d) => {
                    const cell = m.days[d];
                    const locked = !!m.planned_start && `${month}-${String(d).padStart(2, "0")}` < m.planned_start;
                    return (
                      <td key={d} className="p-0.5 text-center">
                        <button type="button" onClick={(ev) => cycle(m, d, ev)} disabled={locked} data-testid={`company-attendance-cell-${m.id}-${d}`}
                          className={cn("flex h-7 w-7 items-center justify-center rounded text-[11px] font-semibold", cell ? (cell.shift === "NIGHT" && cell.status !== "ABSENT" ? NIGHT_CLS : STATUS[cell.status].cls) : "bg-muted/40 text-muted-foreground/40", cell?.billed && "ring-1 ring-sky-400", locked && "cursor-not-allowed opacity-20")}
                          title={locked ? `Job starts ${m.planned_start}` : cell ? `${STATUS[cell.status].full}${cell.shift === "NIGHT" && cell.status !== "ABSENT" ? " · Night shift" : ""}${Number(cell.ot_hours) > 0 ? ` · OT ${cell.ot_hours}h` : ""}${cell.billed ? " · billed" : ""}` : "Not marked"}>
                          {cell ? (cell.shift === "NIGHT" && cell.status !== "ABSENT" ? "N" : STATUS[cell.status].label) : "·"}{cell && Number(cell.ot_hours) > 0 && <sup className="text-[8px]">+{Number(cell.ot_hours)}</sup>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {assignmentId && <GenerateCompanyBill projectId={projectId} assignmentId={assignmentId} assignmentLabel={assignments.find((a) => a.id === assignmentId)?.sr_number} onPaid={load} />}
    </div>
  );
}

function AddWorkersToJobDialog({ projectId, assignmentId, assignmentLabel, existingLabourIds, onAdded }) {
  const [open, setOpen] = useState(false);
  const [labours, setLabours] = useState([]);
  const [pickedIds, setPickedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    api.get(`/projects/${projectId}/labour-master`).then(({ data }) => setLabours((data || []).filter((l) => l.is_active)));
    api.get("/labour-availability", { params: { for_assignment: assignmentId } })
      .then(({ data }) => setBusyIds(new Set((data || []).map((b) => b.labour_id)))).catch(() => setBusyIds(new Set()));
  }, [open, projectId, assignmentId]);

  const available = labours.filter((l) => !existingLabourIds.includes(l.id) && !busyIds.has(l.id));
  const hiddenBusy = labours.filter((l) => !existingLabourIds.includes(l.id) && busyIds.has(l.id)).length;
  const toggle = (id) => setPickedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (pickedIds.length === 0) {
      toast.error("Pick at least one worker");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/labour-management/assignments/${assignmentId}/add-workers`, { labour_ids: pickedIds });
      toast.success(`${data.added} worker(s) added to ${assignmentLabel}`);
      setPickedIds([]);
      setOpen(false);
      onAdded?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button type="button" variant="outline" className="mt-5" data-testid="add-workers-to-job-button"><Plus className="h-4 w-4" /> Add Workers</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Add workers to {assignmentLabel}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="add-workers-to-job-form">
          {available.length === 0 ? (
            <p className="text-xs text-muted-foreground">No free workers for this job's dates. Add a new person in the Labours tab, or extend/complete another job first.</p>
          ) : (
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
              {available.map((l) => (
                <label key={l.id} className="flex items-center gap-2 rounded p-1.5 text-sm hover:bg-muted">
                  <input type="checkbox" checked={pickedIds.includes(l.id)} onChange={() => toggle(l.id)} data-testid={`add-workers-checkbox-${l.id}`} />
                  <span>{l.labour_no ? `${l.labour_no} — ` : ""}{l.name} — {l.trade} — ₹{fmtNum(l.daily_rate)}/day</span>
                </label>
              ))}
            </div>
          )}
          {hiddenBusy > 0 && <p className="text-[11px] text-muted-foreground">{hiddenBusy} worker(s) already on another job for these dates are hidden.</p>}
          <DialogFooter><Button type="submit" disabled={saving || available.length === 0}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GenerateCompanyBill({ projectId, assignmentId, assignmentLabel, onPaid }) {
  const [form, setForm] = useState({ date_from: "", date_to: "", expense_date: todayISO(), paid_from: "PETTY_CASH" });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [busy, setBusy] = useState(null); // "all" | member_id
  const [payAmt, setPayAmt] = useState("");
  const [balances, setBalances] = useState([]);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const base = `/projects/${projectId}/labour-management/assignments/${assignmentId}`;
  const ready = !!form.date_from && !!form.date_to && form.date_to >= form.date_from;

  const loadPreview = useCallback(async () => {
    if (!ready) { setPreview(null); return; }
    setLoadingPreview(true);
    try {
      const { data } = await api.get(`${base}/bill-preview`, { params: { date_from: form.date_from, date_to: form.date_to } });
      setPreview(data);
    } catch (err) {
      setPreview(null);
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPreview(false);
    }
  }, [base, form.date_from, form.date_to, ready]);

  useEffect(() => { loadPreview(); }, [loadPreview]);
  useEffect(() => {
    api.get(`/projects/${projectId}/labour-wage-balances`).then(({ data }) => setBalances(data)).catch(() => {});
  }, [projectId, preview]);
  useEffect(() => { setPayAmt(preview ? String(preview.total) : ""); }, [preview]);

  const rows = preview?.rows || [];
  const fromPetty = form.paid_from === "PETTY_CASH";
  const balance = preview ? Number(preview.petty_balance) : 0;
  const short = (amt) => fromPetty && preview && Number(amt) > balance;

  const pay = async (memberId, amount, who) => {
    if (!(Number(amount) > 0)) { toast.error("Enter an amount to pay"); return; }
    if (short(amount)) { toast.error(`Petty cash at this site is only ₹${fmtNum(balance)}. Top it up first or pay from Bank.`); return; }
    const msg = `Approve & pay ${who} — ₹${fmtNum(amount)} from ${fromPetty ? "petty cash" : "bank"}? This posts a Site Expense (Wages) immediately.`;
    const diff = memberId ? 0 : Number(preview.total) - Number(amount);
    const note = diff > 0 ? ` ₹${fmtNum(diff)} stays owed and is added to the workers' next bill.` : diff < 0 ? ` ₹${fmtNum(-diff)} extra is deducted from the workers' next bill.` : "";
    if (!await confirmAction(msg + note)) return;
    setBusy(memberId || "all");
    try {
      const body = memberId ? { ...form, member_id: memberId } : { ...form, paid_amount: payAmt };
      const { data } = await api.post(`${base}/generate-bill`, body);
      toast.success(`Wages recorded — ₹${fmtNum(data.amount)}${fromPetty ? " (deducted from petty cash)" : ""}. Ref. ${assignmentLabel}.`);
      await loadPreview();
      if (onPaid) onPaid();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]" data-testid="generate-company-bill">
      <div className="rounded-xl border border-border bg-card p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Generate Bill — {assignmentLabel}</p>
        <form onSubmit={(e) => { e.preventDefault(); if (rows.length) pay(null, payAmt, `all workers on ${assignmentLabel}`); }} className="space-y-3" data-testid="generate-company-bill-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From</Label><Input required type="date" value={form.date_from} onChange={set("date_from")} className="mt-1.5" /></div>
            <div><Label>To</Label><Input required type="date" value={form.date_to} onChange={set("date_to")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Expense date</Label><Input required type="date" value={form.expense_date} onChange={set("expense_date")} className="mt-1.5" /></div>
            <div>
              <Label>Paid from</Label>
              <Select value={form.paid_from} onValueChange={(v) => setForm({ ...form, paid_from: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="PETTY_CASH">Petty cash</SelectItem><SelectItem value="BANK">Bank</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          {rows.length > 0 && (
            <div data-testid="pay-amount-field">
              <Label>Amount paying now (₹)</Label>
              <Input type="number" min="0" step="0.01" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="mt-1.5" />
              {Number(payAmt) > 0 && Number(payAmt) !== Number(preview.total) && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {Number(payAmt) < Number(preview.total) ? `₹${fmtNum(Number(preview.total) - Number(payAmt))} stays owed to the workers` : `₹${fmtNum(Number(payAmt) - Number(preview.total))} extra is deducted from their next bill`}
                </p>
              )}
            </div>
          )}
          {fromPetty && preview && (
            <p className="text-xs text-muted-foreground">Petty cash at this site: <span className="font-semibold text-foreground">₹{fmtNum(balance)}</span></p>
          )}
          <Button type="submit" disabled={!rows.length || busy !== null || !(Number(payAmt) > 0) || short(payAmt)} className="w-full" data-testid="generate-company-bill-button">
            {busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve &amp; pay all{rows.length && Number(payAmt) > 0 ? ` — ₹${fmtNum(payAmt)}` : ""}
          </Button>
        </form>
        {balances.length > 0 && (
          <div className="mt-4 border-t border-border pt-3" data-testid="wage-balances">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Wage balances</p>
            <ul className="space-y-1 text-sm">
              {balances.map((b) => (
                <li key={b.labour_id} className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate">{b.name}<span className="text-[10px] text-muted-foreground"> {b.labour_no}</span></span>
                  <span className={`shrink-0 tabular-nums ${Number(b.balance) > 0 ? "text-amber-700 dark:text-amber-400" : "text-emerald-700 dark:text-emerald-400"}`}>
                    {Number(b.balance) > 0 ? "Owed" : "Advance"} ₹{fmtNum(Math.abs(Number(b.balance)))}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11px] text-muted-foreground">From rounded payments. Owed is added to the worker's next bill; Advance is deducted from it.</p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-4" data-testid="company-bill-preview">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bill preview</p>
        {!ready ? (
          <p className="text-sm text-muted-foreground">Pick the From and To dates to see what will be billed.</p>
        ) : loadingPreview && !preview ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No unbilled attendance in this period.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-1.5 pr-2">Worker</th><th className="px-2 text-right">P</th><th className="px-2 text-right">H</th>
                    <th className="px-2 text-right">Night / OT</th><th className="px-2 text-right">Days</th><th className="px-2 text-right">Rate</th><th className="px-2 text-right">Amount</th><th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.member_id} className="border-b border-border/50" data-testid={`bill-preview-row-${r.member_id}`}>
                      <td className="py-1.5 pr-2"><div className="text-foreground">{r.name}</div><div className="text-[10px] text-muted-foreground">{r.labour_no ? `${r.labour_no} · ` : ""}{r.trade}</div></td>
                      <td className="px-2 text-right tabular-nums">{r.present}</td>
                      <td className="px-2 text-right tabular-nums">{r.half_days}</td>
                      <td className="px-2 text-right text-xs tabular-nums text-muted-foreground">{r.night_shifts > 0 ? `${r.night_shifts} night` : ""}{r.night_shifts > 0 && Number(r.ot_hours) > 0 ? " · " : ""}{Number(r.ot_hours) > 0 ? `${Number(r.ot_hours)}h OT` : ""}{r.night_shifts === 0 && !(Number(r.ot_hours) > 0) ? "—" : ""}</td>
                      <td className="px-2 text-right tabular-nums">{fmtNum(r.days)}</td>
                      <td className="px-2 text-right tabular-nums">{fmtNum(r.daily_rate)}</td>
                      <td className="px-2 text-right font-medium tabular-nums">₹{fmtNum(r.amount)}{Number(r.extra) > 0 && <div className="text-[10px] font-normal text-muted-foreground">incl. ₹{fmtNum(r.extra)} night / OT</div>}{Number(r.carry) !== 0 && <div className="text-[10px] font-normal text-muted-foreground">{Number(r.carry) > 0 ? "+ earlier balance" : "− paid in advance"} ₹{fmtNum(Math.abs(Number(r.carry)))}</div>}</td>
                      <td className="pl-2 text-right">
                        <Button type="button" size="sm" variant="outline" disabled={busy !== null || !(Number(r.payable) > 0) || short(r.payable)}
                          onClick={() => pay(r.member_id, r.payable, r.name)} data-testid={`bill-pay-${r.member_id}`}>
                          {busy === r.member_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Pay"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={6} className="pt-2 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total</td>
                    <td className="pt-2 text-right text-base font-semibold tabular-nums">₹{fmtNum(preview.total)}</td><td /></tr>
                </tfoot>
              </table>
            </div>
            {short(preview.total) && (
              <p className="mt-3 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                Petty cash (₹{fmtNum(balance)}) is less than the total. Pay workers one by one while the balance lasts, top up petty cash, or switch to Bank.
              </p>
            )}
            <p className="mt-2 text-[11px] text-muted-foreground">P = present days, H = half days. Paying posts a Site Expense (Wages){fromPetty ? " and deducts it from petty cash" : ""}; those days are then locked as billed.</p>
          </>
        )}
      </div>
    </div>
  );
}

function ShiftMarkBar({ projectId, mark, setMark }) {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/projects/${projectId}/labour-management/pay-rules`).then(({ data }) => setRules(data)).catch(() => {});
  }, [projectId]);
  const setRule = (k) => (e) => setRules({ ...rules, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/labour-management/pay-rules`, {
        ot_multiplier: Number(rules.ot_multiplier), night_multiplier: Number(rules.night_multiplier), std_hours: Number(rules.std_hours),
      });
      setRules(data);
      toast.success("Pay rules saved");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-2 rounded-xl border border-border bg-card p-3" data-testid="shift-mark-bar">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label>Mark as</Label>
          <div className="mt-1.5 inline-flex rounded-lg border border-border bg-muted p-1">
            {[["DAY", "Day"], ["NIGHT", "Night shift"]].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setMark({ ...mark, shift: v })} data-testid={`mark-shift-${v}`}
                className={cn("rounded-md px-3 py-1 text-sm font-medium", mark.shift === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")}>{label}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Overtime hrs</Label>
          <Input type="number" min="0" max="12" step="0.5" value={mark.ot} onChange={(e) => setMark({ ...mark, ot: e.target.value })} className="mt-1.5 w-24" placeholder="0" data-testid="mark-ot-hours" />
        </div>
        <p className="max-w-md text-xs text-muted-foreground">
          Click a cell to cycle P / ½ / A using these settings. <span className="font-medium text-foreground">Shift + click</span> applies them to a marked cell without changing present / half.
        </p>
      </div>
      {rules && (
        <details className="text-sm">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pay rules (this project)</summary>
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <div><Label>Night shift ×</Label><Input type="number" step="0.1" min="1" value={rules.night_multiplier} onChange={setRule("night_multiplier")} className="mt-1.5 w-24" /></div>
            <div><Label>Overtime ×</Label><Input type="number" step="0.1" min="1" value={rules.ot_multiplier} onChange={setRule("ot_multiplier")} className="mt-1.5 w-24" /></div>
            <div><Label>Hours in a day</Label><Input type="number" step="0.5" min="1" value={rules.std_hours} onChange={setRule("std_hours")} className="mt-1.5 w-24" /></div>
            <Button type="button" size="sm" onClick={save} disabled={saving}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Night shift pay = daily rate × night ×. Overtime pay = (OT hrs ÷ hours in a day) × daily rate × overtime ×.</p>
        </details>
      )}
    </div>
  );
}

/* Complete job: the work finished early, so free the workers from the next day. */
function CompleteJobControl({ projectId, assignment, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(today);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOpen(false); setDate(today); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [assignment?.id]);
  if (!assignment) return null;
  const alreadyOver = assignment.planned_end_date <= today;
  const valid = date >= assignment.planned_start_date && date < assignment.planned_end_date;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/labour-management/assignments/${assignment.id}/complete`, { completion_date: date });
      toast.success(`${assignment.sr_number} completed on ${date} - workers are free from the next day`);
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (alreadyOver) {
    return <span className="pb-2 text-xs text-muted-foreground" data-testid="job-ended-note">Job ended {assignment.planned_end_date} - workers are free</span>;
  }
  if (!open) {
    return <Button type="button" variant="outline" onClick={() => setOpen(true)} data-testid="complete-job-button">Complete job</Button>;
  }
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-2" data-testid="complete-job-panel">
      <div>
        <Label>Work finished on</Label>
        <Input type="date" min={assignment.planned_start_date} max={assignment.planned_end_date} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="complete-job-date" />
      </div>
      <Button type="button" disabled={!valid || saving} onClick={submit} data-testid="complete-job-confirm">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Confirm
      </Button>
      <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      <p className="basis-full text-xs text-muted-foreground">
        {valid ? `Planned end moves from ${assignment.planned_end_date} to ${date}. Workers are free for other jobs from the next day. Attendance already marked stays.` : "Pick a date after the job start and before its planned end."}
      </p>
    </div>
  );
}

/* Extend job: the work runs past the planned end. Also warns when attendance has been marked after the planned end. */
function ExtendJobControl({ projectId, assignment, members, month, onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOpen(false); }, [assignment?.id]);
  if (!assignment) return null;

  let lastWorked = "";
  (members || []).filter((m) => m.assignment_id === assignment.id).forEach((m) => {
    Object.entries(m.days || {}).forEach(([d, cell]) => {
      if (cell && cell.status !== "ABSENT") {
        const iso = `${month}-${String(d).padStart(2, "0")}`;
        if (iso > lastWorked) lastWorked = iso;
      }
    });
  });
  const overrun = lastWorked && lastWorked > assignment.planned_end_date;
  const nextDay = (iso) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); };
  const start = () => { setDate(overrun ? lastWorked : nextDay(assignment.planned_end_date)); setOpen(true); };
  const valid = date > assignment.planned_end_date;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/labour-management/assignments/${assignment.id}/extend`, { new_end_date: date });
      toast.success(`${assignment.sr_number} extended to ${date}`);
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {open ? (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-2" data-testid="extend-job-panel">
          <div>
            <Label>New end date</Label>
            <Input type="date" min={nextDay(assignment.planned_end_date)} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="extend-job-date" />
          </div>
          <Button type="button" disabled={!valid || saving} onClick={submit} data-testid="extend-job-confirm">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Confirm
          </Button>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <p className="basis-full text-xs text-muted-foreground">
            {valid ? `The job will run until ${date}. Its workers must be free on the added days, otherwise you will be told who is busy.` : `Pick a date after ${assignment.planned_end_date}.`}
          </p>
        </div>
      ) : (
        <Button type="button" variant={overrun ? "default" : "outline"} onClick={start} data-testid="extend-job-button">Extend job</Button>
      )}
      {overrun && !open && (
        <p className="basis-full text-xs text-amber-600" data-testid="job-overrun-warning">
          Attendance is marked up to {lastWorked}, but this job is planned to end on {assignment.planned_end_date}. Extend the job so its workers stay booked.
        </p>
      )}
    </>
  );
}

/* Clear marks: remove attendance marked by mistake after a date, for every worker on the job. */
function ClearMarksControl({ projectId, assignment, onDone }) {
  const localToday = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(localToday());
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOpen(false); setDate(localToday()); }, [assignment?.id]);
  if (!assignment) return null;

  const submit = async () => {
    if (!await confirmAction(`Remove ALL attendance marked after ${date} for every worker on ${assignment.sr_number}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/labour-management/assignments/${assignment.id}/clear-attendance`, { after_date: date });
      toast.success(data.cleared ? `Cleared ${data.cleared} mark${data.cleared === 1 ? "" : "s"} after ${date}` : `Nothing was marked after ${date}`);
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return <Button type="button" variant="outline" onClick={() => setOpen(true)} data-testid="clear-marks-button">Clear marks…</Button>;
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-2" data-testid="clear-marks-panel">
      <div>
        <Label>Clear everything marked after</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="clear-marks-date" />
      </div>
      <Button type="button" variant="destructive" disabled={!date || saving} onClick={submit} data-testid="clear-marks-confirm">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Clear
      </Button>
      <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      <p className="basis-full text-xs text-muted-foreground">Removes the attendance marks dated after {date || "…"} for all workers on this job. Days already on a bill are never cleared.</p>
    </div>
  );
}
